<template>
  <div class="flex flex-col">
    <div class="flex flex-row flex-wrap justify-stretch items-center px-3 mb-3 sm:px-0">
      <div class="flex flex-col flex-grow">
        <h2 class="text-xl text-left font-bold">
          {{ title }}
        </h2>
      </div>

      <div class="flex flex-col w-full sm:w-auto items-start sm:items-end">
        <div class="flex flex-row items-center">
          <label :for="`${uniqueId}_PageSelector`">Page Size:</label>
          <select
            :id="`${uniqueId}_PageSelector`"
            ref="pageSizeSelector"
            :value="resultsPerPage"
            class="bg-gray-100 p-2 ml-2 focus:outline-none focus:shadow-outline-green"
            @change="loadPage(1, $event.target.value)"
          >
            <option
              v-for="pageSize in pageSizeOptions"
              :key="pageSize"
              :value="pageSize"
            >
              {{ pageSize }}
            </option>
          </select>
        </div>
      </div>
    </div>
    <div class="sm:shadow sm:rounded-lg max-w-fill">
    <div class="align-middle inline-block min-w-full border-b border-gray-200 max-w-full
    overflow-auto text-center">
      <table class="min-w-full">
        <thead class="min-w-full">
        <tr>
          <slot name="header">
            <component
              :id="generateHeaderId(header)"
              :is="`th`"
              v-for="header in headers"
              :key="generateHeaderId(header)"
              class="px-4 py-2 bg-green-500 text-white hover:opacity-75 cursor-pointer w-1 whitespace-no-wrap"
              :class="[{
                'sort-column': header.value === sortValue,
                'sort-up': reverseItems,
                'pointer-events-none': !header.sortable,
              }, headerClass]"
              @click="sortable ? sortItems(header.value) : false"
              v-html="typeof header === 'string' ? header : header.label"
            >

            </component>
          </slot>
        </tr>
        </thead>
        <tbody class="bg-white">
        <slot name="body">
          <component
            :is="`tr`"
            v-for="(item, i) in activeItems"
            :key="`${item}_${i}`"
            class="h-12 max-h-12 whitespace-no-wrap"
          >
            <slot
              name="item"
              :item="item"
            >
            </slot>
          </component>
        </slot>
        </tbody>
        <tfoot>
        <slot name="footer"></slot>
        </tfoot>
      </table>
    </div>

    <Pagination
      :current-page="currentPage"
      :page-size="resultsPerPage"
      :total-pages="totalPages"
      :total-items="tableItems.length"
      @loadPage="loadPage"
    />
    </div>
  </div>
</template>

<script>
import Utils from '../_mixins/Utils';
import Pagination from './Pagination.vue';

export default {
  name: 'SimpleTable',
  mixins: [Utils],
  components: { Pagination },
  props: {
    title: {
      type: String,
      default: () => null,
    },
    headers: {
      type: Array,
      default: () => [],
    },
    headerClass: {
      type: String,
      default: () => null,
    },
    items: {
      type: Array,
      default: () => [],
    },
    sortable: {
      type: Boolean,
      default: () => false,
    },
    page: {
      type: Number,
      default: () => 1,
    },
    pageSize: {
      type: Number,
      default: () => Number(localStorage.getItem('resultsPerPage')),
    },
    pageSizeOptions: {
      type: Array,
      default: () => [10, 20, 50, 100],
    },
  },
  data() {
    return {
      tableItems: this.items || [],
      currentPage: this.page || 1,
      resultsPerPage: this.pageSize || 20,
      reverseItems: false,
      sortValue: null,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.tableItems.length / this.resultsPerPage);
    },
    sortedItems() {
      const defaultHeader = this.headers.find((header) => header.default);
      const newItems = this.tableItems
        ? this.dynamicSort(this.tableItems, this.sortValue || defaultHeader.value) : [];
      if (this.reverseItems) {
        newItems.reverse();
      }
      return newItems || [];
    },
    activeItems() {
      const start = (this.currentPage - 1) * this.resultsPerPage;
      return this.sortedItems.slice(start, start + this.resultsPerPage);
    },
  },
  methods: {
    loadPage(page, pageSize = this.resultsPerPage) {
      this.currentPage = page;
      if (pageSize !== this.resultsPerPage) {
        localStorage.setItem('resultsPerPage', String(pageSize));
      }
      this.resultsPerPage = Number(pageSize);
    },
    sortItems(value, reverseItems = false) {
      let reverse = reverseItems;
      if (this.sortValue === value) {
        reverse = !this.reverseItems;
      }
      this.sortValue = value;
      this.reverseItems = reverse;
      this.$emit('sortChange', this.sortValue);
    },
    dynamicSort(array, key) {
      return array.sort((a, b) => {
        function pluckValue(value) {
          return key
            .split('.')
            .reduce((o, i) => o[i], value);
        }

        function parseValue(value) {
          const parsedResult = parseFloat(value);
          return Number.isNaN(parsedResult) ? null : parsedResult;
        }

        let A = pluckValue(a);
        let B = pluckValue(b);
        const parsedA = parseValue(A);
        const parsedB = parseValue(B);

        if (
          (typeof A === 'string' && typeof B === 'string')
            && (typeof parsedA === typeof parsedB)
            && (typeof parsedA !== 'number')
        ) {
          A = A.toLowerCase();
          B = B.toLowerCase();

          if (A > B) {
            return 1;
          }

          if (A === B) {
            return 0;
          }

          return -1;
        }

        return parsedB - parsedA;
      });
    },
    generateHeaderId(header) {
      return `${this.uniqueId}_TableHeader_${
        (typeof header === 'string' ? header : header.label)
          .replace('<br /> ', '')
          .replace(/ /g, '_')
      }`;
    },
  },
  watch: {
    items(val) {
      this.tableItems = val;
    },
  },
  mounted() {
    this.loadPage(this.page);
    this.headers.forEach((header) => {
      if (header.default && header.sortable) {
        this.sortItems(header.value, header.reverse);
      }
    });
  },
};
</script>

<style lang="scss">
  th.sort-column {
    position: relative;

    &:after {
      content: " ";
      bottom: 50%;
      height: 10px;
      width: 10px;
      right: 8px;
      border: 5px solid transparent;
      position: absolute;
      pointer-events: none;
      border-bottom-color: #fff;
      margin-left: -5px;
      transition: transform .2s ease-in-out;
      transform-origin: center 8px;
      transform: rotate(180deg);
    }

    &.sort-up {
      &:after {
        transform: rotate(0);
      }
    }
  }

</style>
